/**
 * Utility functions for safe cookie parsing
 * Handles cookies that may be prefixed with "base64-" or encoded
 */

// Track cookie parse failures to avoid log spam (max one warning per cookie type per session)
const loggedParseFailures = new Set<string>();

/**
 * Log cookie parsing issues - always logs in non-production, rate-limited in production
 */
function logCookieWarning(message: string, identifier: string): void {
  // Always log in development
  if (process.env.NODE_ENV === 'development') {
    console.warn(`[Cookie Parse Warning] ${message}`);
    return;
  }

  // In production, only log each unique failure once per session to prevent log spam
  if (!loggedParseFailures.has(identifier)) {
    loggedParseFailures.add(identifier);
    console.warn(`[Cookie Parse Warning] ${message}`);
  }
}

/**
 * Safely parse a cookie value that may be JSON or base64-encoded JSON
 * @param value - The cookie value to parse
 * @returns The parsed value or null if parsing fails
 */
export function safeParseCookie(value: string | undefined): any {
  if (!value || typeof value !== 'string') return null;

  // Trim the value to remove any whitespace
  const trimmedValue = value.trim();

  // If the value is empty after trimming, return null
  if (!trimmedValue) return null;

  // Create a safe identifier for logging (first 10 chars, masked)
  const valuePrefix = trimmedValue.substring(0, 10);

  try {
    // Check if the value starts with "base64-" (case-insensitive for robustness)
    if (trimmedValue.toLowerCase().startsWith('base64-')) {
      // Remove the "base64-" prefix
      const base64String = trimmedValue.substring(7);

      // Check if the base64 string looks valid (has content)
      if (base64String && base64String.length > 3) {
        try {
          // Decode from base64
          // Note: eyJ... is valid base64 - it's the encoding of {"... (JSON object start)
          const decodedString = atob(base64String);

          // Try to parse as JSON
          return JSON.parse(decodedString);
        } catch (base64Error) {
          // If base64 decoding or JSON parsing fails, try returning the decoded string
          try {
            const decodedString = atob(base64String);
            return decodedString;
          } catch (decodeError) {
            // Base64 decoding failed entirely - this is an auth-impacting failure
            logCookieWarning(
              `Base64 decode failed for cookie starting with "${valuePrefix}..." - this may cause auth issues`,
              `base64-decode-${valuePrefix}`
            );
            return null;
          }
        }
      } else {
        // Invalid or truncated base64 string - log as this can cause auth failures
        logCookieWarning(
          `Truncated base64 cookie detected (length: ${base64String?.length || 0}) - may cause session loss`,
          `truncated-${valuePrefix}`
        );
        return null;
      }
    }

    // Check if it looks like JSON (starts with { or [)
    if (trimmedValue.startsWith('{') || trimmedValue.startsWith('[')) {
      // Try parsing as regular JSON
      try {
        return JSON.parse(trimmedValue);
      } catch (jsonError) {
        // JSON parse failed - log as this impacts auth
        logCookieWarning(
          `JSON parse failed for cookie starting with "${valuePrefix}..." - malformed auth cookie may cause logout`,
          `json-parse-${valuePrefix}`
        );
        // Return the original value as fallback to prevent complete auth failure
        return trimmedValue;
      }
    }

    // If it doesn't look like JSON, return the value as is
    return trimmedValue;
  } catch (error) {
    // Unexpected error during parsing - log with details
    logCookieWarning(
      `Unexpected error parsing cookie starting with "${valuePrefix}...": ${error instanceof Error ? error.message : 'Unknown error'}`,
      `unexpected-${valuePrefix}`
    );
    // Return the original value to prevent complete auth failure
    return trimmedValue;
  }
}

/**
 * Encode a value for storage in a cookie
 * @param value - The value to encode
 * @returns The encoded string
 */
export function encodeCookieValue(value: any): string {
  try {
    const jsonString = JSON.stringify(value);
    // For consistency, we'll use base64 encoding
    const base64String = btoa(jsonString);
    return `base64-${base64String}`;
  } catch (error) {
    console.error('Failed to encode cookie value');
    // Fallback to plain JSON string
    return JSON.stringify(value);
  }
}